<template>
  <div>
    <mf-info-card
      v-if="canAlterTradeOffers"
      class="mb-2"
      title="Aviso sobre alteração"
      description="Você pode alterar o nome e prazos desta campanha porque é um administrador Marcafacil. Essas alterações não estão disponíveis para o varejista."
    />

    <v-card class="mb-2 colored-border" :style="{ borderColor: getStatusColor }">
      <div class="header-with-tag">
        <div class="trade-tag" :style="{ color: getTagColor, borderColor: getTagColor, background: getBackroundTagColor }">
          <span>{{ isMercapromo ? 'Mercapromo' : 'Mercatrade' }}</span>
        </div>
        <div>
          <v-card-title class="titulo-card">{{ canAlterTradeOffers ? titleForAlteration : campaign.name }}</v-card-title>
          <v-card-subtitle class="subtitulo-card">{{ canAlterTradeOffers ? getFormat : campaign.description }}</v-card-subtitle>
        </div>
      </div>

      <div v-if="canAlterTradeOffers" class="d-flex flex-row">
        <div style="flex: 1">
          <v-text-field
            v-model="campaign.name"
            class="mt-4 ml-4"
            required
            outlined
            maxlength="200"
            label="Título da campanha"
            counter
            :error-messages="nameErrors"
            :disabled="includesThisStatus(['finished', 'expired', 'industry_rejected', 'processing'])"
            @input="$v.campaign.name.$touch()"
            @blur="$v.campaign.name.$touch()"
          />
        </div>

        <div class="mt-4 mx-4" style="flex: 2">
          <v-text-field v-model="campaign.description" outlined disabled counter maxlength="200" label="Descrição" persistent-placeholder />
        </div>
      </div>

      <div v-if="canAlterTradeOffers" class="d-flex flex-row">
        <div class="ml-4" style="width: 312px">
          <mf-date-range-picker
            style="width: 100%"
            label="Período de validade"
            icon
            outlined
            disable-typing
            :edit-only-end-date="campaign.status === 'active'"
            :min="todayDate"
            :initial-date.sync="campaign.start_date"
            :final-date.sync="campaign.end_date"
            :error="campaignDateErrors.length > 0"
            :error-messages="campaignDateErrors"
            :disabled="includesThisStatus(['scheduled', 'active', 'finished', 'expired', 'industry_rejected', 'processing'])"
            :status="campaign.status"
          />
        </div>

        <div class="mx-4" style="width: 312px">
          <date-select
            v-model="campaign.industry.proposal_deadline"
            label="Prazo de adesão"
            type="date"
            hint="(*) o horário final sempre será as 23:59 do dia escolhido"
            persistent-hint
            :error="proposalDeadlineErrors.length > 0"
            :error-messages="proposalDeadlineErrors"
            :max="campaignEndDate"
            :disabled="includesThisStatus(['scheduled', 'active', 'finished', 'expired', 'industry_rejected', 'processing'])"
            hide-action-buttons
            @change="() => $emit('update:proposalDeadlineErrors', [])"
          />
        </div>

        <div v-if="isNextbuyCashback || isCashbackAbsolute" class="mr-4" style="width: 312px">
          <date-select
            v-model="cashback_expiration_date"
            label="Validade do cashback"
            type="date"
            :error="cashbackExpirationDateErrors.length > 0"
            :error-messages="cashbackExpirationDateErrors"
            :min="campaignEndDate"
            :disabled="includesThisStatus(['scheduled', 'active', 'finished', 'expired', 'industry_rejected', 'processing'])"
            hide-action-buttons
            @change="updateCashbackExpirationDate"
          />
        </div>
      </div>

      <v-card-text class="d-flex flex-wrap" :style="isMobile ? 'gap: 24px' : 'gap: 12px'">
        <info-card
          v-for="card in cardsItems"
          :key="card.key"
          style="width: 49%"
          :title="card.title"
          :description="card.value"
          :tooltip="card.tooltip"
          :card-key="card.key"
        />
        <info-card style="width: 49%" title="Lojas participantes" :description="infoCardUnitsDescription" card-key="units">
          <template #right-content>
            <mf-select-units-dialog
              id="campaign-units"
              template-type="button"
              btn-content="Alterar lojas"
              :disabled="includesThisStatus(['processing'])"
              :disable-save="includesThisStatus(['finished', 'expired', 'industry_rejected', 'processing']) || isDisabledInRules(rules.units)"
              :selected-units.sync="campaign.units"
              :selected-units-configuration.sync="campaign.units_configuration"
              :units="units"
              :unavailable-units="unavailableUnits"
              unavailable-tooltip="Esta loja não pode participar dessa oferta"
              :tag-list="tagList"
              :group-list="unitsGroups"
              :default-data-type="defaultDataType"
              hide-ecommerce-configuration
            />
          </template>
          <template v-if="unitsError.length > 0" #additional-content>
            <span style="font-size: 12px; color: #ff5252">{{ unitsError.join(', ') }}</span>
          </template>
        </info-card>
      </v-card-text>
    </v-card>
  </div>
</template>

<script>
import { DateTime } from 'luxon'
import { getStatusLabel } from '@/config/helpers.js'
import { mapGetters } from 'vuex'
import { rules } from '@/modules/campanhas/gestao-ofertas-v2/helpers/rules.js'
import { getProductFormatTypeLabel } from '@/modules/mobile/ofertas/helpers/productType.js'
import InfoCard from '@/components/campaigns/InfoCard.vue'
import { hasPostCommunication, getPostCommunicationType } from '@/modules/industria/helpers/postCommunication.js'
import DateSelect from '@/components/DateSelect.vue'
import { required, minLength, maxLength } from 'vuelidate/lib/validators'
import { notOnlySpace } from '@/helpers/validators.js'

export default {
  name: 'HeaderTrade',
  components: {
    InfoCard,
    DateSelect,
  },
  props: {
    campaign: {
      type: Object,
      required: true,
    },
    isDisabledInRules: {
      type: Function,
      default: () => false,
    },
    proposalDeadlineErrors: {
      type: Array,
      default: () => [],
    },
    campaignDateErrors: {
      type: Array,
      default: () => [],
    },
    cashbackExpirationDateErrors: {
      type: Array,
      default: () => [],
    },
    canAlterTradeOffers: {
      type: Boolean,
      default: false,
    },
  },
  data: () => ({
    rules,
    cashback_expiration_date: '',
  }),
  computed: {
    ...mapGetters(['units', 'tagList', 'unitsGroups']),

    cardsItems() {
      const cardsItems = [
        {
          key: 'validity-period',
          title: 'Período de validade',
          value: this.getVisualDate,
          tooltip: '',
          hasSlot: false,
        },
        {
          key: 'proposal-deadline',
          title: 'Prazo de adesão',
          value: this.getVisualProposalDeadline,
          tooltip: '',
          hasSlot: false,
        },
        {
          key: 'campaign-format',
          title: 'Formato',
          value: this.getFormat,
          tooltip: '',
          hasSlot: false,
        },
        {
          key: 'campaign-industry',
          title: 'Indústria',
          value: this.campaign.industry.name,
          tooltip: '',
          hasSlot: false,
        },
        ...((this.isNextbuyCashback || this.isCashbackAbsolute) && !this.canAlterTradeOffers
          ? [
              {
                key: 'cashback-expiration-date',
                title: 'Validade do Cashback',
                value: this.getCashbackExpirationDate,
                tooltip: '',
                hasSlot: false,
              },
            ]
          : []),
        ...(this.isMercapromo && this.hasPostCommunication(this.campaign)
          ? [
              {
                key: 'communication',
                title: 'Comunicação',
                value: this.getPostCommunicationType(this.campaign),
                tooltip: '',
                hasSlot: false,
              },
            ]
          : []),
      ]

      if (this.canAlterTradeOffers) {
        const validityPeriodIndex = cardsItems.findIndex((item) => item.key === 'validity-period')
        cardsItems.splice(validityPeriodIndex, 2)
      }

      return cardsItems
    },

    todayDate() {
      return DateTime.now().toFormat('yyyy-MM-dd')
    },

    campaignEndDate() {
      return DateTime.fromISO(this.campaign.end_date).toFormat('yyyy-MM-dd')
    },

    getVisualDate() {
      const startDate = DateTime.fromISO(this.campaign.start_date).toFormat('dd/MM/yyyy')
      const endDate = DateTime.fromISO(this.campaign.end_date).toFormat('dd/MM/yyyy')
      return `${startDate} - ${endDate}`
    },

    getVisualProposalDeadline() {
      if (this.campaign?.industry?.proposal_deadline) {
        const date = DateTime.fromISO(this.campaign.industry.proposal_deadline).toFormat('dd/MM/yyyy HH:mm')
        return `${date}`
      }
      return '-'
    },

    getFormat() {
      if (!this.campaign.types) return '-'
      return this.campaign.types.length > 1 ? 'Múltiplos' : getProductFormatTypeLabel(this.campaign.types[0])
    },

    getCashbackExpirationDate() {
      const { next_buy_cashback_offer_sales, cashback_absolute_offer_sales } = this.campaign
      if (next_buy_cashback_offer_sales?.[0]?.cashback_expiration_date) {
        return DateTime.fromISO(next_buy_cashback_offer_sales?.[0]?.cashback_expiration_date).toFormat('dd/MM/yyyy')
      } else if (cashback_absolute_offer_sales?.[0]?.expiration_cashback?.expires_at) {
        return DateTime.fromISO(cashback_absolute_offer_sales?.[0]?.expiration_cashback?.expires_at).toFormat('dd/MM/yyyy')
      }
      return '-'
    },

    getStatusColor() {
      return getStatusLabel(this.campaign?.status).color
    },

    unavailableUnits() {
      const allUnitsErp = this.units.map((u) => u.id_erp)
      return allUnitsErp.filter((unit) => !this.campaign?.industry?.available_units?.includes(unit))
    },

    defaultDataType() {
      if (this.campaign?.units_configuration?.length) {
        return this.campaign?.units_configuration[0]?.dataType
      }
      return null
    },

    isNextbuyCashback() {
      return this.campaign?.next_buy_cashback_offer_sales?.length > 0
    },

    isCashbackAbsolute() {
      return this.campaign?.cashback_absolute_offer_sales?.length > 0
    },

    infoCardUnitsDescription() {
      const units = this.campaign?.units?.length
      let text = 'selecionada'
      if (units) {
        if (units > 1) {
          text = 'selecionadas'
        }
        return `${units} ${text}`
      }
      return 'Nenhuma loja'
    },

    isMercapromo() {
      return Boolean(this.campaign?.industry?.is_mercapromo)
    },

    nameErrors() {
      const errors = []
      if (!this.$v.campaign.name.$dirty) return errors
      !this.$v.campaign.name.required && errors.push('Este campo é obrigatório.')
      !this.$v.campaign.name.minLength && errors.push('Este campo deve conter pelo menos 2 caracteres.')
      !this.$v.campaign.name.maxLength && errors.push('Este campo deve conter no máximo 200 caracteres.')
      !this.$v.campaign.name.notOnlySpace && errors.push('Este campo não pode conter somente espaços.')
      return errors
    },

    unitsError() {
      const errors = []
      if (!this.$v.campaign.units.$dirty) return errors
      !this.$v.campaign.units.required && errors.push('Selecione ao menos uma loja.')
      return errors
    },

    titleForAlteration() {
      return this.isMercapromo ? 'Oferta Mercapromo' : 'Oferta Mercatrade'
    },

    getTagColor() {
      return this.isMercapromo ? '#C3008C' : '#5B0A9B'
    },

    getBackroundTagColor() {
      return this.isMercapromo ? '#c3008c1c' : '#5b0a9b17'
    },
  },
  watch: {
    'campaign.start_date'(newValue, oldValue) {
      this.$emit('changeStartDate')
    },
    'campaign.end_date'(newValue, oldValue) {
      this.$emit('changeEndDate')
    },
  },
  mounted() {
    if (this.isNextbuyCashback && this.campaign?.next_buy_cashback_offer_sales?.[0]?.cashback_expiration_date) {
      this.cashback_expiration_date = this.campaign.next_buy_cashback_offer_sales[0].cashback_expiration_date
    } else if (this.isCashbackAbsolute && this.campaign?.cashback_absolute_offer_sales?.[0]?.expiration_cashback?.expires_at) {
      this.cashback_expiration_date = this.campaign.cashback_absolute_offer_sales[0].expiration_cashback.expires_at
    }
  },
  methods: {
    hasPostCommunication,
    getPostCommunicationType,

    includesThisStatus(status) {
      return status.includes(this.campaign.status)
    },

    updateCashbackExpirationDate(newDate) {
      if (this.isNextbuyCashback) {
        this.campaign.next_buy_cashback_offer_sales.forEach((offer) => {
          offer.cashback_expiration_date = newDate
        })
      } else if (this.isCashbackAbsolute) {
        this.campaign.cashback_absolute_offer_sales.forEach((offer) => {
          offer.expiration_cashback.expires_at = newDate
        })
      }

      this.$emit('update:cashbackExpirationDateErrors', [])
    },

    validNameAndUnits() {
      this.$v.$reset()
      this.$v.$touch()
      if (this.$v.$error || this.$v.$invalid) {
        this.$snackbar({ message: 'Preencha todos os campos corretamente', snackbarColor: 'error' })
        return false
      }
      return true
    },
  },
  validations: {
    campaign: {
      name: {
        required,
        minLength: minLength(2),
        maxLength: maxLength(200),
        notOnlySpace,
      },
      units: {
        required,
      },
    },
  },
}
</script>

<style lang="scss" scoped>
.colored-border {
  border-left: 6px solid;
}

.header-with-tag {
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  align-items: center;
}

.titulo-card {
  font-size: 20px;
  font-weight: bold;
  color: $cinza-d20;
}

.subtitulo-card {
  font-size: 16px;
  color: $cinza-d20;
}

.trade-tag {
  margin-left: 18px;
  width: 100px;
  height: 36px;
  border: 1px solid;
  border-radius: 6px;
  font-size: 13px;
  display: flex;
  justify-content: center;
  align-items: center;
  font-weight: bold;
}
</style>
